import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Plus, Edit, Trash2, Image, FileText, Layers, Play, Clock, Link as LinkIcon, ClipboardList } from 'lucide-react';
import { Card, Button, Badge, LoadingSpinner, ConfirmDialog } from '../../components/common';
import { useAdminContent } from '../../hooks/useAdminContent';
// import { CreateSubcategoryModal, CreateProductModal } from '../../components/admin/AdminContentModals';
import { CreateSubcategoryModal } from '@/components/admin/CreateSubcategoryModal';
import { CreateProductModal } from '@/components/admin/CreateProductModal';
import { CreateVideoModal, UpdateVideoModal } from '../../components/admin/AdminVideoModals';
import { SubcategoryHierarchy, CreateSubcategoryData, CreateProductData, CreateVideoData, UpdateVideoData, Video } from '../../types';
import api from '../../services/api';

import { UpdateSubcategoryModal } from '@/components/admin/UpdateSubcategoryModal';
import { subcategoryService } from '../../services/api';

import { UpdateProductModal } from '@/components/admin/UpdateProductModal';
import { Product } from '../../types';

import QuickActions from '@/components/admin/QuickActions';

import TestsTab from '@/components/admin/TestsTab';
import { CreateTestQuestionModal } from '@/components/admin/CreateTestQuestionModal';
import { UpdateTestQuestionModal } from '@/components/admin/UpdateTestQuestionModal';
import { AdminTestQuestion, CreateTestQuestionData, UpdateTestQuestionData } from '../../types';

const AdminContent: React.FC = () => {
  const {
    activeTab,
    setActiveTab,
    selectedCategoryId,
    setSelectedCategoryId,
    categories,
    allSubcategories,
    allProducts,
    videos,
    testQuestions,
    isLoadingCategories,
    isLoadingVideos,
    isLoadingTests,
    createSubcategory,
    createProduct,
    createVideo,
    updateVideo,
    deleteSubcategory,
    deleteProduct,
    deleteVideo,
    createTestQuestion,
    updateTestQuestion,
    deleteTestQuestion,
    isCreatingSubcategory,
    isCreatingProduct,
    isCreatingVideo,
    isUpdatingVideo,
    isCreatingTestQuestion,
    isUpdatingTestQuestion,
    getCategoryStats,
    refreshData,    
    isUpdatingProduct,
  } = useAdminContent();

  // Estados de modales
  const [showCreateSubcategoryModal, setShowCreateSubcategoryModal] = useState(false);
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [showCreateVideoModal, setShowCreateVideoModal] = useState(false);
  const [showUpdateVideoModal, setShowUpdateVideoModal] = useState(false);

  const [showUpdateSubcategoryModal, setShowUpdateSubcategoryModal] = useState(false);
  const [subcategoryToEdit, setSubcategoryToEdit] = useState<SubcategoryHierarchy | null>(null);

  const [showUpdateProductModal, setShowUpdateProductModal] = useState(false);
  const [productToEdit, setProductToEdit] = useState<Product | null>(null);
  // const [refreshFlag, setRefreshFlag] = useState(0);

  const [videoToEdit, setVideoToEdit] = useState<Video | null>(null);

  // Estados de test questions
  const [showCreateTest, setShowCreateTest] = useState(false);
  const [showUpdateTest, setShowUpdateTest] = useState(false);
  const [testToEdit, setTestToEdit] = useState<AdminTestQuestion | null>(null);

  const [itemToDelete, setItemToDelete] = useState<{
    type: 'subcategory' | 'product' | 'video' | 'testQuestion';
    id: string;
    name: string;
    categoryId?: string;     // 👈 NUEVO (para subcategorías y preguntas de test)
    subcategoryId?: string;  // 👈 NUEVO (para productos)
  } | null>(null);

  // Filtros para mostrar datos según la tab activa
  const filteredSubcategories = selectedCategoryId
    ? allSubcategories.filter(sub => sub.categoryId === selectedCategoryId)
    : allSubcategories;

  const filteredVideos = selectedCategoryId
    ? videos.filter(video => video.categoryId === selectedCategoryId)
    : videos;

  const tabs = [
    { id: 'categories' as const, label: 'Categorías', icon: Package },
    { id: 'subcategories' as const, label: 'Subcategorías', icon: FileText },
    { id: 'products' as const, label: 'Productos', icon: Image },
    { id: 'videos' as const, label: 'Videos', icon: Play },
    { id: 'tests' as const, label: 'Tests', icon: ClipboardList }
  ];

  const API_ORIGIN: string = (() => {
    try {
      const base = (api?.defaults?.baseURL as string) || '';
      return base ? new URL(base).origin : window.location.origin;
    } catch {
      return window.location.origin;
    }
  })();

  // Si la URL viene relativa (/uploads/...), la convierte a absoluta con el backend.
  const resolveImageUrl = (u?: string) => {
    if (!u) return '';
    if (/^https?:\/\//i.test(u)) return u;       // ya es absoluta
    if (u.startsWith('/uploads/')) return `${API_ORIGIN}${u}`;
    return u;                                     // cualquier otra ruta, déjala igual
  };

  // === NUEVO: resolver URL de icono de subcategoría leyendo iconUrl o icon_url, y resolviendo rutas relativas
  const getSubcategoryIconUrl = (sub: any): string => {
    const raw = sub?.iconUrl ?? sub?.icon_url ?? '';
    return resolveImageUrl(raw || '');
  };

  // Manejar eliminación
  // Manejar eliminación
  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      if (itemToDelete.type === 'subcategory') {
        // ✅ ahora pasamos objeto { id, categoryId? }
        await deleteSubcategory({
          id: itemToDelete.id,
          categoryId: itemToDelete.categoryId,
        });
      } else if (itemToDelete.type === 'product') {
        // ✅ ahora pasamos objeto { id, subcategoryId? }
        await deleteProduct({
          id: itemToDelete.id,
          subcategoryId: itemToDelete.subcategoryId,
        });
      } else if (itemToDelete.type === 'video') {
        // este sigue recibiendo solo el id
        await deleteVideo(itemToDelete.id);
      } else if (itemToDelete.type === 'testQuestion') {
        await deleteTestQuestion({
          id: itemToDelete.id,
          categoryId: itemToDelete.categoryId!,
        });
      }
      setItemToDelete(null);
      refreshData();
    } catch (error) {
      // El error se maneja en el hook
    }
  };

  // Manejar edición de video
  const handleEditVideo = (video: Video) => {
    setVideoToEdit(video);
    setShowUpdateVideoModal(true);
  };

  // Funciones wrapper para los modales
  const handleCreateSubcategory = async (data: CreateSubcategoryData) => {
    await createSubcategory(data);
  };

  const handleEditSubcategory = (subcategory: SubcategoryHierarchy) => {
    setSubcategoryToEdit(subcategory);
    setShowUpdateSubcategoryModal(true);
  };

  const handleCreateProduct = async (data: CreateProductData) => {
    await createProduct(data);
  };

  const handleEditProduct = (product: Product) => {
    setProductToEdit(product);
    setShowUpdateProductModal(true);
  };  

  const handleCreateVideo = async (data: CreateVideoData) => {
    await createVideo(data);
  };

  const handleUpdateVideo = async (id: string, data: UpdateVideoData) => {
    await updateVideo({ id, data });
  };

  // Funciones wrapper para test questions
  const handleCreateTest = async (data: CreateTestQuestionData) => {
    await createTestQuestion(data);
  };

  const handleUpdateTest = async (id: string, catId: string, data: UpdateTestQuestionData) => {
    await updateTestQuestion({ id, categoryId: catId, data });
  };

  const handleEditTest = (question: AdminTestQuestion) => {
    setTestToEdit(question);
    setShowUpdateTest(true);
  };

  // Función para extraer ID de YouTube y crear URL embebida
  const getYouTubeEmbedUrl = (url: string): string => {
    const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
    const match = url.match(regex);
    const videoId = match ? match[1] : null;
    return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
  };

  // Función para formatear duración
  const formatDuration = (seconds?: number): string => {
    if (!seconds) return 'No especificada';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Renderizar nivel de indentación para subcategorías
  const renderSubcategoryLevel = (level: number = 0) => {
    const colors = [
      'text-blue-600',
      'text-green-600',
      'text-purple-600',
      'text-orange-600'
    ];

    return (
      <div className="flex items-center">
        {Array.from({ length: level }).map((_, i) => (
          <div key={i} className="w-4 h-4 mr-1">
            <div className="w-0.5 h-full bg-gray-300 ml-2"></div>
          </div>
        ))}
        <Layers className={`w-4 h-4 ${colors[level] || 'text-gray-600'}`} />
        <span className={`ml-1 text-xs font-medium ${colors[level] || 'text-gray-600'}`}>
          Nivel {level + 1}
        </span>
      </div>
    );
  };

  if (isLoadingCategories) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-brinsa-azul mb-2">
              Gestión de Contenido
            </h1>
            <p className="text-lg text-brinsa-azulOscuro/80">
              Administra categorías, subcategorías, productos, videos y tests
            </p>
          </div>

          <div className="flex space-x-3">
            {activeTab === 'subcategories' && (
              <Button
                onClick={() => setShowCreateSubcategoryModal(true)}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Crear Subcategoría
              </Button>
            )}
            {activeTab === 'products' && (
              <Button
                onClick={() => setShowCreateProductModal(true)}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Crear Producto
              </Button>
            )}
            {activeTab === 'videos' && (
              <Button
                onClick={() => setShowCreateVideoModal(true)}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Crear Video
              </Button>
            )}
            {activeTab === 'tests' && (
              <Button
                onClick={() => setShowCreateTest(true)}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Nueva Pregunta
              </Button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <Card className="border border-brinsa-celeste/30">
          {/* 🔧 Responsive: scroll horizontal en móviles para que no se rompa cuando hay muchas tabs */}
          <div
            className="border-b border-brinsa-celeste/30 -mx-2 px-2 overflow-x-auto"
            style={{ scrollbarWidth: 'thin' }}
          >
            <div className="flex gap-2 md:gap-0 whitespace-nowrap min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                  className={[
                    'flex items-center gap-2 px-4 md:px-5 py-3 text-sm font-medium rounded-t-lg transition-colors flex-shrink-0',
                    activeTab === tab.id
                      ? 'text-brinsa-azul border-b-2 border-brinsa-azul bg-brinsa-celeste/10'
                      : 'text-brinsa-azulOscuro/70 hover:text-brinsa-azul hover:bg-brinsa-celeste/10'
                  ].join(' ')}
                >
                  <tab.icon className="w-4 h-4 flex-shrink-0" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Contenido de las tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        {/* Tab de categorías (sin cambios de lógica) */}
        {activeTab === 'categories' && (
          <Card title="Categorías (Solo lectura)" className="border border-brinsa-celeste/30">
            <div className="space-y-4">
              {categories.map((category) => {
                const stats = getCategoryStats(category.id);
                return (
                  // 🔧 Responsive: stack en mobile para que el "Tipo:" no se salga
                  <div
                    key={category.id}
                    className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 border rounded-lg transition-all
                               border-brinsa-celeste/30 hover:border-brinsa-azul hover:bg-brinsa-celeste/10"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 rounded-lg bg-brinsa-celeste/30 flex items-center justify-center">
                        {category.iconUrl ? (
                          <img
                            src={category.iconUrl}
                            alt={category.name}
                            className="w-8 h-8 object-contain"
                            onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
                          />
                        ) : (
                          <Package className="w-6 h-6 text-brinsa-azul" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-brinsa-azul">{category.name}</h3>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                          <span className="text-xs text-brinsa-azulOscuro/70">
                            {stats.subcategories} subcategorías
                          </span>
                          <span className="text-xs text-brinsa-azulOscuro/70">
                            {stats.products} productos
                          </span>
                          <span className="text-xs text-brinsa-azulOscuro/70">
                            {videos.filter(v => v.categoryId === category.id).length} videos
                          </span>
                          <Badge variant="success" size="sm">Activa</Badge>
                        </div>
                        {category.description && (
                          <p className="text-sm text-brinsa-azulOscuro/70 mt-1">
                            {category.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* 🔧 En mobile se ubica debajo, en md+ a la derecha */}
                    <div className="md:text-right">
                      <p className="text-xs text-brinsa-azulOscuro/60 whitespace-nowrap">
                        <span className="font-medium">Tipo:</span> {category.type}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Tab de subcategorías (AJUSTE: icono visible si hay URL) */}
        {activeTab === 'subcategories' && (
          <div className="space-y-4">
            {/* Filtro por categoría */}
            <Card className="border border-brinsa-celeste/30">
              <div className="p-4">
                <label className="block text-sm font-medium text-brinsa-azul mb-2">
                  Filtrar por categoría:
                </label>
                <select
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul focus:border-transparent"
                >
                  <option value="">Todas las categorías</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </Card>

            <Card title="Subcategorías" className="border border-brinsa-celeste/30">
              <div className="space-y-4">
                {filteredSubcategories.length === 0 ? (
                  <div className="text-center py-8 text-brinsa-azulOscuro/70">
                    {selectedCategoryId
                      ? 'No hay subcategorías en esta categoría'
                      : 'No hay subcategorías creadas'
                    }
                  </div>
                ) : (
                  filteredSubcategories.map((subcategory) => {
                    const subIconUrl = getSubcategoryIconUrl(subcategory);
                    return (
                      // 🔧 Responsive: stack en mobile para que acciones/tipo no se desborden
                      <div
                        key={subcategory.id}
                        className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 border rounded-lg transition-all
                                   border-brinsa-celeste/30 hover:border-brinsa-azul hover:bg-brinsa-celeste/10"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 rounded-lg bg-brinsa-celeste/30 flex items-center justify-center overflow-hidden">
                            {subIconUrl ? (
                              <img
                                src={subIconUrl}
                                alt={subcategory.name}
                                className="w-7 h-7 object-contain"
                                onError={(e) => {
                                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                                  const fallback = e.currentTarget.nextElementSibling as HTMLDivElement | null;
                                  if (fallback) fallback.style.display = 'grid';
                                }}
                              />
                            ) : null}
                            {/* Fallback por defecto si no hay URL o si la imagen falló */}
                            <div className="w-full h-full hidden place-items-center">
                              <FileText className="w-5 h-5 text-brinsa-azul" />
                            </div>
                            {!subIconUrl && (
                              <FileText className="w-5 h-5 text-brinsa-azul" />
                            )}
                          </div>

                          <div>
                            <div className="flex items-center space-x-2">
                              <h3 className="font-semibold text-brinsa-azul">{subcategory.name}</h3>
                              <Badge variant="default" size="sm">{subcategory.size}</Badge>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                              {renderSubcategoryLevel(subcategory.level)}
                              <span className="text-xs text-brinsa-azulOscuro/70">
                                {subcategory.categoryName}
                              </span>
                              <span className="text-xs text-brinsa-azulOscuro/70 whitespace-nowrap">
                                {subcategory.productCount || 0} productos
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2 md:self-auto self-start">
                          <Button
                            variant="outline"
                            size="sm"
                            className="hover:border-brinsa-azul"
                            onClick={() => handleEditSubcategory(subcategory)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>

                          <Button
                            variant="outline"
                            size="sm"
                            className="hover:border-red-500 hover:text-red-600"
                            onClick={() => setItemToDelete({
                              type: 'subcategory',
                              id: subcategory.id,
                              name: subcategory.name,
                              categoryId: subcategory.categoryId,
                            })}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Card>
          </div>
        )}

        {/* ============================================================
            TAB DE PRODUCTOS (con filtro por categoría)
        ============================================================ */}
        {activeTab === 'products' && (
          <div className="space-y-4">
            {/* 🔽 Filtro por categoría */}
            <Card className="border border-brinsa-celeste/30">
              <div className="p-4">
                <label className="block text-sm font-medium text-brinsa-azul mb-2">
                  Filtrar por categoría:
                </label>
                <select
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul focus:border-transparent"
                >
                  <option value="">Todas las categorías</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </Card>

            {/* 🧱 Grid de productos filtrados */}
            <Card
              title="Productos"
              className="border border-brinsa-celeste/30"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allProducts.length === 0 ? (
                  <div className="col-span-full text-center py-8 text-brinsa-azulOscuro/70">
                    No hay productos creados
                  </div>
                ) : (
                  allProducts
                    .filter((p) => !selectedCategoryId || p.categoryId === selectedCategoryId)
                    .map((product) => (
                      <div
                        key={product.id}
                        className="border rounded-lg p-4 transition-all
                                  border-brinsa-celeste/30 hover:border-brinsa-azul hover:bg-brinsa-celeste/10"
                      >
                        {/* Imagen */}
                        <div
                          className="w-full h-32 rounded-lg mb-4
                                    bg-gradient-to-br from-white to-brinsa-celeste/20
                                    border border-brinsa-celeste/30
                                    overflow-hidden flex items-center justify-center p-2"
                        >
                          {product.imageUrl ? (
                             <img
                                src={`${resolveImageUrl(product.imageUrl)}?v=${product.updatedAt || Date.now()}`}
                                alt={product.name}
                                className="max-w-full max-h-full object-contain"
                                onError={(e) => {
                                  console.warn('Error cargando imagen:', product.imageUrl);
                                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                                }}
                              />
                          ) : (
                            <Image className="w-8 h-8 text-brinsa-azul" />
                          )}
                        </div>

                        {/* Info */}
                        <h3 className="font-semibold text-brinsa-azul mb-2">{product.name}</h3>
                        <div className="space-y-1 text-sm text-brinsa-azulOscuro/80 mb-4">
                          <div>
                            <span className="font-medium text-brinsa-azul">Categoría:</span>{' '}
                            {product.categoryName}
                          </div>
                          <div>
                            <span className="font-medium text-brinsa-azul">Subcategoría:</span>{' '}
                            {product.subcategoryPath}
                          </div>
                          {product.internalCode && (
                            <div>
                              <span className="font-medium text-brinsa-azul">Código:</span>{' '}
                              {product.internalCode}
                            </div>
                          )}
                        </div>

                        {product.description && (
                          <p className="text-sm text-brinsa-azulOscuro/70 mb-4 line-clamp-2">
                            {product.description}
                          </p>
                        )}

                        {/* Botones */}
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 hover:border-brinsa-azul"
                            onClick={() => handleEditProduct(product)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>

                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 hover:border-red-500 hover:text-red-600"
                            onClick={() =>
                              setItemToDelete({
                                type: 'product',
                                id: product.id,
                                name: product.name,
                                subcategoryId: (product as any)?.subcategoryId,
                              })
                            }
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </Card>
          </div>
        )}

        {/* NUEVA Tab de videos */}
        {activeTab === 'videos' && (
          <div className="space-y-4">
            {/* Filtro por categoría para videos */}
            <Card className="border border-brinsa-celeste/30">
              <div className="p-4">
                <label className="block text-sm font-medium text-brinsa-azul mb-2">
                  Filtrar por categoría:
                </label>
                <select
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul focus:border-transparent"
                >
                  <option value="">Todas las categorías</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </Card>

            <Card title="Videos" className="border border-brinsa-celeste/30">
              {isLoadingVideos ? (
                <div className="flex justify-center items-center py-8">
                  <LoadingSpinner size="md" />
                  <span className="ml-2 text-brinsa-azulOscuro/80">Cargando videos...</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {filteredVideos.length === 0 ? (
                    <div className="col-span-full text-center py-8 text-brinsa-azulOscuro/70">
                      {selectedCategoryId
                        ? 'No hay videos en esta categoría'
                        : 'No hay videos creados'
                      }
                    </div>
                  ) : (
                    filteredVideos.map((video) => (
                      <div
                        key={video.id}
                        className="border rounded-lg p-4 transition-all
                                   border-brinsa-celeste/30 hover:border-brinsa-azul hover:bg-brinsa-celeste/10"
                      >
                        {/* Preview del video */}
                        <div className="relative w-full h-40 rounded-lg overflow-hidden mb-4 bg-black">
                          {getYouTubeEmbedUrl(video.youtubeUrl) ? (
                            <iframe
                              src={getYouTubeEmbedUrl(video.youtubeUrl)}
                              title={video.title}
                              className="w-full h-full"
                              frameBorder="0"
                              allowFullScreen
                            />
                          ) : (
                            <div className="w-full h-full bg-brinsa-celeste/10 flex items-center justify-center">
                              <Play className="w-12 h-12 text-brinsa-azul/60" />
                            </div>
                          )}
                        </div>

                        {/* Información del video */}
                        <div className="space-y-3">
                          <div>
                            <h3 className="font-semibold text-brinsa-azul mb-1">{video.title}</h3>
                            <Badge variant="default" size="sm">
                              {video.category?.name}
                            </Badge>
                          </div>

                          {video.description && (
                            <p className="text-sm text-brinsa-azulOscuro/80 line-clamp-2">
                              {video.description}
                            </p>
                          )}

                          <div className="flex items-center gap-4 text-xs text-brinsa-azulOscuro/70">
                            <div className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {formatDuration(video.duration)}
                            </div>
                            <div className="flex items-center">
                              <LinkIcon className="w-3 h-3 mr-1" />
                              YouTube
                            </div>
                          </div>

                          {/* Botones de acción */}
                          <div className="flex gap-2 pt-2 border-t border-brinsa-celeste/30">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 hover:border-brinsa-azul"
                              onClick={() => handleEditVideo(video)}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Editar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="hover:border-red-500 hover:text-red-600"
                              onClick={() => setItemToDelete({
                                type: 'video',
                                id: video.id,
                                name: video.title
                              })}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* NUEVA Tab de tests */}
        {activeTab === 'tests' && (
          <TestsTab
            categories={categories}
            testQuestions={testQuestions}
            selectedCategoryId={selectedCategoryId}
            isLoading={isLoadingTests}
            onSelectCategory={setSelectedCategoryId}
            onEdit={handleEditTest}
            onDelete={setItemToDelete}
          />
        )}
      </motion.div>

      {/* Modales existentes */}
      <CreateSubcategoryModal
        isOpen={showCreateSubcategoryModal}
        onClose={() => setShowCreateSubcategoryModal(false)}
        categories={categories}
        allSubcategories={allSubcategories}
        onSubmit={handleCreateSubcategory}
        isSubmitting={isCreatingSubcategory}
      />

      <UpdateSubcategoryModal
        isOpen={showUpdateSubcategoryModal}
        onClose={() => {
          setShowUpdateSubcategoryModal(false);
          setSubcategoryToEdit(null);
        }}
        subcategory={subcategoryToEdit}
        categories={categories}
        allSubcategories={allSubcategories}
        onUpdated={async () => {
          refreshData();
        }}
        isSubmitting={isCreatingSubcategory}
      />

      

      <CreateProductModal
        isOpen={showCreateProductModal}
        onClose={() => setShowCreateProductModal(false)}
        categories={categories}
        allSubcategories={allSubcategories}
        onSubmit={handleCreateProduct}
        isSubmitting={isCreatingProduct}
      />

      <UpdateProductModal
        isOpen={showUpdateProductModal}
        onClose={() => {
          setShowUpdateProductModal(false);
          setProductToEdit(null);
        }}
        product={productToEdit}
        categories={categories}
        allSubcategories={allSubcategories}
        onUpdated={refreshData}
        isSubmitting={isCreatingProduct || isUpdatingProduct}
      />

      {/* NUEVOS Modales para videos */}
      <CreateVideoModal
        isOpen={showCreateVideoModal}
        onClose={() => setShowCreateVideoModal(false)}
        categories={categories}
        onSubmit={handleCreateVideo}
        isSubmitting={isCreatingVideo}
      />

      <UpdateVideoModal
        isOpen={showUpdateVideoModal}
        onClose={() => {
          setShowUpdateVideoModal(false);
          setVideoToEdit(null);
        }}
        video={videoToEdit}
        onSubmit={handleUpdateVideo}
        isSubmitting={isUpdatingVideo}
      />

      {/* NUEVOS Modales para test questions */}
      <CreateTestQuestionModal
        isOpen={showCreateTest}
        onClose={() => setShowCreateTest(false)}
        categories={categories}
        onSubmit={handleCreateTest}
        isSubmitting={isCreatingTestQuestion}
      />

      <UpdateTestQuestionModal
        isOpen={showUpdateTest}
        onClose={() => {
          setShowUpdateTest(false);
          setTestToEdit(null);
        }}
        question={testToEdit}
        onSubmit={handleUpdateTest}
        isSubmitting={isUpdatingTestQuestion}
      />

      <QuickActions />

      {/* Confirmación de eliminación */}
      <ConfirmDialog
        isOpen={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        onConfirm={handleDelete}
        title={`Eliminar ${itemToDelete?.type === 'subcategory' ? 'Subcategoría' :
          itemToDelete?.type === 'product' ? 'Producto' :
          itemToDelete?.type === 'testQuestion' ? 'Pregunta' : 'Video'
          }`}
        message={`¿Estás seguro de que deseas eliminar "${itemToDelete?.name}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
      />

    </div>
  );
};

export default AdminContent;
